/**
 * Wave 4 — Proposal cockpit: sections + commercial value (domain APIs).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  InputNumber,
  List,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EPC_COST_GROUPS } from '@/constants/epc-cost-groups';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatMoney } from '@/utils/format';
import {
  buildProposalReadiness,
  normalizeSections,
  readinessTagColor,
  seedCommercialSections,
  seedTechnicalSections,
  type StageReadinessState,
} from './estimateProposalSubmissionReadiness';

const { Text, Title } = Typography;

export type EpcProposalCockpitProps = {
  tenderId: string;
  proposal: Record<string, unknown> | null;
  currency?: string;
  onCreated?: (row: Record<string, unknown>) => void;
  onUpdated?: () => void;
};

export const EpcProposalCockpit: React.FC<EpcProposalCockpitProps> = ({
  tenderId,
  proposal,
  currency = 'AED',
  onCreated,
  onUpdated,
}) => {
  const qc = useQueryClient();
  const id = proposal ? String(proposal.id || '') : '';
  const [valueDraft, setValueDraft] = useState<number | null>(null);

  useEffect(() => {
    setValueDraft(
      proposal?.commercial_value != null ? Number(proposal.commercial_value) : null
    );
  }, [proposal?.id, proposal?.commercial_value]);

  const createM = useMutation({
    mutationFn: () =>
      epcCommercialService.createProposal({
        tenderId,
        currency,
        technicalSectionsJson: seedTechnicalSections(),
        commercialSectionsJson: seedCommercialSections(EPC_COST_GROUPS),
      }),
    onSuccess: (row) => {
      message.success('Proposal created');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onCreated?.(row);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const patchM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcCommercialService.patchProposal(id, payload),
    onSuccess: () => {
      message.success('Proposal updated');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const readiness = useMemo(
    () => (proposal ? buildProposalReadiness(proposal) : null),
    [proposal]
  );

  const tech = proposal
    ? normalizeSections(proposal.technical_sections_json ?? proposal.technicalSectionsJson)
    : [];
  const comm = proposal
    ? normalizeSections(proposal.commercial_sections_json ?? proposal.commercialSectionsJson)
    : [];

  const toggleSection = (
    kind: 'technical' | 'commercial',
    key: string,
    complete: boolean
  ) => {
    if (!proposal || !id) return;
    const seeded =
      kind === 'technical'
        ? seedTechnicalSections().map((s) => ({ key: s.key, complete: false }))
        : seedCommercialSections(EPC_COST_GROUPS).map((s) => ({
            key: s.key,
            complete: false,
          }));
    const current = (kind === 'technical' ? tech : comm).length
      ? kind === 'technical'
        ? [...tech]
        : [...comm]
      : seeded;
    const next = current.map((s) => ({
      key: s.key,
      status:
        s.key === key ? (complete ? 'complete' : 'missing') : s.complete ? 'complete' : 'missing',
    }));
    if (!next.some((s) => s.key === key)) {
      next.push({ key, status: complete ? 'complete' : 'missing' });
    }
    const payload =
      kind === 'technical'
        ? { technicalSectionsJson: next }
        : { commercialSectionsJson: next };
    patchM.mutate(payload);
  };

  if (!proposal) {
    return (
      <Card size="small" title="Proposal cockpit">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No proposal for this tender yet"
          description="Create a draft with seeded technical + commercial section checklists."
        />
        <Button type="primary" loading={createM.isPending} onClick={() => createM.mutate()}>
          Create proposal
        </Button>
      </Card>
    );
  }

  const editable = !['Submitted', 'Approved', 'Issued'].includes(String(proposal.status || ''));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space wrap>
        <Title level={5} style={{ margin: 0 }}>
          {String(proposal.proposal_number || 'Proposal')}
        </Title>
        {readiness ? (
          <Tag color={readinessTagColor(readiness.overall as StageReadinessState)}>
            {readiness.overall}
          </Tag>
        ) : null}
        <Tag>{String(proposal.status || 'Draft')}</Tag>
        <Text type="secondary">
          {proposal.commercial_value != null
            ? formatMoney(Number(proposal.commercial_value), {
                currency: String(proposal.currency || currency),
              })
            : 'No commercial value'}
        </Text>
      </Space>

      <Card size="small" title="Commercial value">
        <Space wrap>
          <InputNumber
            style={{ minWidth: 200 }}
            disabled={!editable || patchM.isPending}
            min={0}
            value={valueDraft ?? undefined}
            addonAfter={String(proposal.currency || currency)}
            onChange={(n) => setValueDraft(n == null ? null : Number(n))}
            aria-label="Commercial value"
          />
          <Button
            disabled={!editable || valueDraft == null || patchM.isPending}
            loading={patchM.isPending}
            onClick={() => {
              if (valueDraft == null) return;
              patchM.mutate({ commercialValue: valueDraft });
            }}
          >
            Save value
          </Button>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Primary input to award reconciliation — set from estimate, not invented %.
        </Text>
      </Card>

      <Card size="small" title="Technical sections">
        <List
          size="small"
          dataSource={
            tech.length
              ? tech
              : seedTechnicalSections().map((s) => ({ key: s.key, complete: false }))
          }
          renderItem={(s) => (
            <List.Item>
              <Checkbox
                checked={s.complete}
                disabled={!editable || patchM.isPending}
                onChange={(e) => toggleSection('technical', s.key, e.target.checked)}
              >
                {s.key}
              </Checkbox>
            </List.Item>
          )}
        />
      </Card>

      <Card size="small" title="Commercial sections (cost groups)">
        <List
          size="small"
          dataSource={
            comm.length
              ? comm
              : seedCommercialSections(EPC_COST_GROUPS).map((s) => ({
                  key: s.key,
                  complete: false,
                }))
          }
          renderItem={(s) => (
            <List.Item>
              <Checkbox
                checked={s.complete}
                disabled={!editable || patchM.isPending}
                onChange={(e) => toggleSection('commercial', s.key, e.target.checked)}
              >
                {s.key}
              </Checkbox>
            </List.Item>
          )}
        />
      </Card>

      {readiness ? (
        <Card size="small" title="Readiness groups">
          <List
            size="small"
            dataSource={readiness.groups}
            renderItem={(g) => (
              <List.Item>
                <Text>
                  {g.name}: <Tag color={readinessTagColor(g.state)}>{g.state}</Tag>
                </Text>
              </List.Item>
            )}
          />
        </Card>
      ) : null}
    </Space>
  );
};

export default EpcProposalCockpit;
